import * as Sentry from '@sentry/nextjs';

import { PAYMENT_METHOD_PAYMENT_TYPES, PAYMENT_TYPES } from './config';
import { UnknownPaymentMethodError } from './errors';

// These are intentionally minimal, duck-typed shapes covering only the
// fields we actually read here, rather than the full Stripe SDK types.
// The full types require every field Stripe could ever send (e.g.
// `billing_details`, `customer_account`), which is unnecessarily strict
// for this normalization function and would fight with our test/story
// fixtures, which intentionally only mock the fields we use.
interface LegacyCard {
  object: string;
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  wallet?: unknown;
}

interface LegacyBankAccount {
  object: string;
  id: string;
  account_holder_type: string | null;
  account_type?: string | null;
}

interface ModernPaymentMethod {
  object: string;
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
    funding?: string;
    wallet?: unknown;
  };
  us_bank_account?: {
    account_holder_type?: string | null;
    account_type?: string | null;
    last4?: string | null;
    routing_number?: string | null;
    bank_name?: string | null;
  };
  link?: {
    email?: string | null;
    persistent_token?: string | null;
  };
}

type LegacySource = LegacyCard | LegacyBankAccount;
type DefaultPaymentMethod = ModernPaymentMethod | LegacySource;

export interface NormalizedCardPaymentMethod {
  type: 'card';
  paymentMethod?: true;
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  funding?: string;
  wallet: unknown;
}

export interface NormalizedBankAccountPaymentMethod {
  type: 'bank_account';
  id: string;
  accountHolderType: string | null;
  accountType?: string | null;
}

export interface NormalizedUsBankAccountPaymentMethod {
  type: 'us_bank_account';
  paymentMethod: true;
  id: string;
  accountHolderType: string | null;
  accountType: string | null;
  last4: string | null;
  routingNumber: string | null;
  bankName: string | null;
}

export interface NormalizedLinkPaymentMethod {
  type: 'link';
  paymentMethod: true;
  id: string;
  email: string | null;
  persistentToken: string | null;
}

export interface NormalizedCashAppPaymentMethod {
  type: 'cashapp';
  paymentMethod: true;
  id: string;
}

export interface NormalizedAmazonPayPaymentMethod {
  type: 'amazon_pay';
  paymentMethod: true;
  id: string;
}

export type NormalizedPaymentMethod =
  | NormalizedCardPaymentMethod
  | NormalizedBankAccountPaymentMethod
  | NormalizedUsBankAccountPaymentMethod
  | NormalizedLinkPaymentMethod
  | NormalizedCashAppPaymentMethod
  | NormalizedAmazonPayPaymentMethod;

// This is a bit of a legacy object, so we convert it to the lowercased
// variant in use now with PaymentMethods.
// https://stripe.com/docs/api/cards
const convertLegacyCardToPaymentMethod = (cardBrand: string): string => {
  switch (cardBrand) {
    case 'American Express':
      return 'amex';
    case 'Diners Club':
      return 'diners';
    case 'Discover':
      return 'discover';
    case 'Eftpos Australia':
      return 'eftpos';
    case 'JCB':
      return 'jcb';
    case 'MasterCard':
      return 'mastercard';
    case 'UnionPay':
      return 'unionpay';
    case 'Visa':
      return 'visa';
    default:
      return cardBrand;
  }
};

// Customer payment info can be in two different places depending if they used the legacy Card or a newer PaymentMethod
const normalizePaymentMethod = ({
  defaultPaymentMethod,
  defaultSource,
}: {
  defaultPaymentMethod?: DefaultPaymentMethod | string | null;
  defaultSource?: LegacySource | string | null;
}): NormalizedPaymentMethod | null => {
  // First we need to check if we have a default method, prioritizing default_payment_method, then default_source.
  // These could be a Card, a PaymentMethod, or a Source - any of which could be a card, bank account, etc.
  // We need to check for the existence of the card property to determine if it's a card.
  const paymentMethod = defaultPaymentMethod || defaultSource;

  Sentry.addBreadcrumb({
    category: 'stripe:parsePaymentMethod',
    message: 'Parse payment method data to normalized format',
    data: {
      defaultPaymentMethod,
      defaultSource,
    },
  });

  if (!paymentMethod || typeof paymentMethod === 'string') {
    return null;
  }

  // Legacy Payment Types such as Card, and Source
  if (paymentMethod.object === PAYMENT_TYPES.CARD) {
    const card = paymentMethod as LegacyCard;
    return {
      type: PAYMENT_TYPES.CARD as 'card',
      id: card.id,
      brand: convertLegacyCardToPaymentMethod(card.brand),
      last4: card.last4,
      expMonth: card.exp_month,
      expYear: card.exp_year,
      wallet: card.wallet,
    };
  }

  if (paymentMethod.object === PAYMENT_TYPES.BANK_ACCOUNT) {
    const bankAccount = paymentMethod as LegacyBankAccount;
    return {
      type: PAYMENT_TYPES.BANK_ACCOUNT as 'bank_account',
      id: bankAccount.id,
      accountHolderType: bankAccount.account_holder_type,
      accountType: bankAccount.account_type,
    };
  }

  // These are for the modern "PaymentMethod" types.
  if (paymentMethod.object === PAYMENT_TYPES.PAYMENT_METHOD) {
    const pm = paymentMethod as ModernPaymentMethod;
    const paymentMethodPaymentType = pm.type;
    if (paymentMethodPaymentType === PAYMENT_METHOD_PAYMENT_TYPES.CARD) {
      return {
        type: PAYMENT_METHOD_PAYMENT_TYPES.CARD as 'card',
        paymentMethod: true,
        id: pm.id,
        brand: pm.card?.brand,
        last4: pm.card?.last4,
        expMonth: pm.card?.exp_month,
        expYear: pm.card?.exp_year,
        funding: pm.card?.funding,
        wallet: pm.card?.wallet,
      } as NormalizedCardPaymentMethod;
    }
    if (
      paymentMethodPaymentType === PAYMENT_METHOD_PAYMENT_TYPES.US_BANK_ACCOUNT
    ) {
      return {
        type: PAYMENT_METHOD_PAYMENT_TYPES.US_BANK_ACCOUNT as 'us_bank_account',
        paymentMethod: true,
        id: pm.id,
        accountHolderType: pm.us_bank_account?.account_holder_type ?? null,
        accountType: pm.us_bank_account?.account_type ?? null,
        last4: pm.us_bank_account?.last4 ?? null,
        routingNumber: pm.us_bank_account?.routing_number ?? null,
        bankName: pm.us_bank_account?.bank_name ?? null,
      };
    }
    if (paymentMethodPaymentType === PAYMENT_METHOD_PAYMENT_TYPES.LINK) {
      return {
        id: pm.id,
        type: PAYMENT_METHOD_PAYMENT_TYPES.LINK as 'link',
        paymentMethod: true,
        email: pm.link?.email ?? null,
        persistentToken: pm.link?.persistent_token || null, // This is undefined in test mode, it seems
      };
    }
    if (paymentMethodPaymentType === PAYMENT_METHOD_PAYMENT_TYPES.CASH_APP) {
      return {
        id: pm.id,
        type: PAYMENT_METHOD_PAYMENT_TYPES.CASH_APP as 'cashapp',
        paymentMethod: true,
        // There is no more detail on this method.
      };
    }
    if (paymentMethodPaymentType === PAYMENT_METHOD_PAYMENT_TYPES.AMAZON_PAY) {
      return {
        id: pm.id,
        type: PAYMENT_METHOD_PAYMENT_TYPES.AMAZON_PAY as 'amazon_pay',
        paymentMethod: true,
        // There is no more detail on this method.
      };
    }
  }

  // Report to sentry a type we haven't seen before
  Sentry.captureException(
    new UnknownPaymentMethodError(
      paymentMethod.object,
      (paymentMethod as ModernPaymentMethod).type,
      `Unexpected \`${paymentMethod.object}\` type \`${(paymentMethod as ModernPaymentMethod).type}\``
    )
  );

  return null;
};

export { normalizePaymentMethod };
